"use client";

import React, { Children, ReactNode } from "react";

import { motion } from "framer-motion";

function ContentWithStyles({ children }: { children: ReactNode }) {
  const list = Children.toArray(children);

  if (list.length > 1)
    return Children.map(children, (child) => (
      <ContentWithStyles>{child}</ContentWithStyles>
    ));

  const child = list[0];

  if (!child || typeof child !== "object" || !("props" in child)) return child;

  const element = child as React.JSX.Element;

  if (element.type === "img") {
    return (
      <div className="mt-6 flex w-full justify-center">
        {React.cloneElement(element, { className: "w-full rounded-[30px]" })}
      </div>
    );
  } else if (element.type === "p") {
    // special case for image as it comes from CMS
    if (element.props.children?.type === "img")
      return <ContentWithStyles>{element.props.children}</ContentWithStyles>;

    return React.cloneElement(element, {
      className:
        "text-[18px] mb-4 mt-4 text-left text-base font-normal leading-[32px] text-gray-300",
    });
  } else if (element.type === "h2") {
    return React.cloneElement(element, {
      className: "mt-6 text-xl font-semibold",
    });
  } else if (element.type === "ul") {
    return React.cloneElement(element, {
      className: "mt-2 list-disc pl-5 text-gray-300",
    });
  }

  return React.cloneElement(element, {
    children: <ContentWithStyles>{element.props.children}</ContentWithStyles>,
  });
}

const ContentSection = ({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) => {
  return (
    <motion.section
      className="module-content mx-auto max-w-[850px] px-6 lg:px-0"
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <h2 className="my-9 text-[48px] font-semibold leading-[1.3]">
        {heading}
      </h2>
      <ContentWithStyles>{children}</ContentWithStyles>
    </motion.section>
  );
};

export default ContentSection;
